<template>
  <div
    class="mw-home-card-container p-3 d-flex justify-content-center"
    @click="goToCase"
  >
    <div class="mw-home-card">
      <!-- ROOT CASE HEADER -->
      <div
        class="mw-home-card-header"
        :class="{ 'mw-home-card-header-display': display }"
      >
        <div
          class="mw-home-card-header-title"
          v-if="display"
          @click="goToCase($event, true)"
        >
          <IconWrapper
            :size="20"
            class="mw-home-card-header-icon"
            :icon="cardIcon"
            :miniIcon="miniIcon"
          />
          <TextEllipsis class="mw-home-card-header-label" :text="label" />
        </div>
        <!-- LOOKUP -->
        <button
          class="mw-home-card-lookup"
          :class="{ 'mw-home-card-lookup-display': display }"
          @click.stop="display = !display"
          @mouseenter="display = true"
        >
          <font-awesome-icon :icon="['fal', display ? 'times' : 'eye']" />
        </button>
      </div>

      <!-- ROOT CASE CHILDREN -->
      <div v-if="display" class="mw-home-card-children">
        <div v-if="loading" class="mw-home-card-children-loader">
          <font-awesome-icon :icon="['fal', 'spinner-third']" spin />
        </div>
        <div v-else-if="orderedChildren && orderedChildren.length">
          <MaydayWebHomeCardItem
            v-for="child in orderedChildren"
            :key="child.id"
            v-bind="child"
          />
        </div>
        <div v-else class="mw-home-card-children-empty">
          <font-awesome-icon
            class="mw-home-card-children-empty-icon"
            :icon="['fal', 'folder-times']"
          />
          {{ $t('mayday-web.case-empty') }}
        </div>
      </div>
      <!-- ROOT CASE INFO -->
      <div v-else class="mw-home-card-info">
        <IconWrapper class="mw-home-card-icon" :icon="cardIcon" />
        <TextOverflowTooltip
          class="mw-home-card-label"
          :text="label"
          :line-clamp="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import IconWrapper from '@/components/IconWrapper';

import { mapActions, mapGetters } from 'vuex';
import TextEllipsis from '@/components/TextEllipsis';
import MaydayWebHomeCardItem from './MaydayWebHomeCardItem';
import TextOverflowTooltip from '@/components/Commons/TextOverflowTooltip';
import emojiStrip from '@/utils/emojiStrip';

export default {
  name: 'MaydayWebHomeCard',
  components: {
    IconWrapper,
    TextEllipsis,
    MaydayWebHomeCardItem,
    TextOverflowTooltip,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: Object,
      default: null,
    },
    miniIcon: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      display: false,
      children: null,
      loading: false,
    };
  },
  computed: {
    cardIcon() {
      return this.icon && this.icon.value
        ? this.icon
        : {
            iconType: 'fa',
            value: 'folder',
            color: null,
          };
    },
    orderedChildren() {
      if (!this.children) return [];
      if (!this.isSplitKnowledgeViewer) return this.children;
      return [...this.children].sort((a, b) =>
        emojiStrip(a.label).localeCompare(emojiStrip(b.label)),
      );
    },
    ...mapGetters(['isSplitKnowledgeViewer']),
    ...mapGetters('webKnowledgeModule', ['focusKnowledge']),
  },
  methods: {
    goToCase(e, force = false) {
      if (this.display && !force) return;

      this.goToWebTranslatedEntity({
        entityId: this.id,
        entityType: 'Case',
        query: { source: 'navigation' },
        open: e.metaKey || e.ctrlKey,
      });
    },
    async getChildren() {
      if (!this.children) {
        this.loading = true;
        this.children = await this.fetchCaseChildrenById(this.id);
        this.loading = false;
      }
    },
    ...mapActions('webKnowledgeModule', [
      'fetchCaseChildrenById',
      'goToWebTranslatedEntity',
    ]),
  },
  watch: {
    display(newValue) {
      if (newValue) {
        return this.getChildren();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
button {
  background: transparent;
  border: none;
  outline: none;
}

.mw-home-card {
  background-color: white;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 5px 15px $shadow-4-mayday;
  cursor: pointer;
  color: $grey-4-mayday;
  height: 200px;
  width: 250px;
  &:hover {
    box-shadow: 0px 10px 30px $shadow-4-mayday;
    .mw-home-card-lookup {
      opacity: 1;
    }
  }

  &-label {
    display: flex;
    text-align: center;
    justify-content: center;
    color: $blue-9-mayday;
    margin: 0;
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;
  }

  &-icon {
    margin: 0 0 32px 0;
  }

  &-header {
    display: flex;
    align-items: center;
    width: 250px;
    background-color: transparent;
    justify-content: flex-end;

    height: 40px;
    padding: 0 12px;

    &-display {
      background-color: $grey-1-mayday;
      justify-content: space-between;
    }
    &-title {
      display: flex;
      align-items: center;
      gap: 8px;
      width: calc(100% - 12px);
      animation: fade-in 250ms ease-in-out;
    }
    &-label {
      font-weight: 700;
      color: $blue-9-mayday;
      margin-right: 4px;
    }
  }

  &-lookup {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0 0 0;
    opacity: 0;
    align-self: flex-start;
    color: $grey-8-mayday;
    font-size: 14px;
    width: 14px;
    height: 14px;
    transition: opacity 250ms ease-in-out;
    &:hover {
      color: $blue-mayday;
    }
    &-display {
      opacity: 1;
    }
  }

  &-info {
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &-children {
    animation: fade-in 250ms ease-in-out;
    height: 160px;
    width: 250px;
    overflow: auto;
    &-loader {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 48px;
      width: 100%;
      height: 100%;
    }
    &-empty {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 8px;
      font-size: 14px;
      color: $grey-6-mayday;
      width: 100%;
      height: 100%;
      &-icon {
        font-size: 48px;
        color: $grey-4-mayday;
      }
    }
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@media (max-width: 992px) {
  .mw-home-card-container,
  .mw-home-card,
  .mw-home-card-children,
  .mw-home-card-header {
    width: 100%;
  }
}
</style>
